/**
 * A multi-turn tool loop on Bedrock.
 *
 * WHY THIS EXISTS
 * ---------------
 * The package had two shapes of model call and neither is agentic. `invokeTool`
 * forces exactly one tool with `tool_choice: {type:"any"}`, which is the right
 * guarantee for a router or a structured card and the wrong one for a question
 * that needs three lookups before it can be answered. `streamChat` sends no
 * tools at all. Anything that had to call a tool, read the result, and then
 * decide what to do next had nowhere to go.
 *
 * Anthropic's hosted MCP connector does that loop server-side, but it is a
 * first-party API feature and does not exist on Bedrock. On Bedrock the loop is
 * the caller's job, so it lives here once rather than being hand-rolled per
 * caller — the same reason invokeTool was consolidated.
 *
 * THE CONTRACT
 * ------------
 * Each step is one InvokeModel call with `tool_choice: {type:"auto"}`: the
 * model may call one or more tools OR answer in text. Tool calls are executed
 * through the caller's `runTool`, fed back as `tool_result` blocks, and the
 * loop goes round again. It ends when the model returns a step with no tool
 * calls (an answer) or when `maxSteps` steps have run, whichever comes first.
 *
 * `maxSteps` is a hard ceiling, not a target. A model that keeps calling tools
 * forever costs real money, so hitting the ceiling returns normally with
 * `hitStepCeiling: true` and whatever text the last step produced. A caller
 * that treats an empty answer as a pass rather than a failure is reading the
 * flag wrong.
 *
 * A tool that throws is NOT a loop failure. The error message goes back as a
 * `tool_result` with `is_error: true` and the model gets to react, which is how
 * a wrong argument becomes a retry instead of a dead run.
 *
 * `attribution` and `onUsage` behave exactly as they do in invokeTool:
 * attribution rides the signed `X-Amzn-Bedrock-Request-Metadata` header on
 * every step, and `onUsage` is called once per step with THAT step's counts.
 * The accumulated total for the whole loop is on the result as `usage`.
 */

import {
  BEDROCK_TIMEOUT_MS,
  invokeModelRaw,
  type BedrockCredentials,
  type BedrockToolDefinition,
} from "./bedrock.js";
import {
  ZERO_USAGE,
  addUsage,
  extractUsage,
  type AiAttribution,
  type TokenUsage,
} from "./attribution.js";

/** Anthropic content blocks, the subset a tool loop produces or consumes. */
export type ToolLoopBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: Record<string, unknown> }
  | { type: "tool_result"; tool_use_id: string; content: string; is_error?: boolean };

export interface ToolLoopMessage {
  role: "user" | "assistant";
  content: string | ToolLoopBlock[];
}

/** One tool the model asked for. `id` must come back on the matching result. */
export interface ToolLoopCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ToolLoopRequest {
  system: string;
  messages: ToolLoopMessage[];
  /**
   * Ordinary Anthropic tool definitions. Mark the LAST one with
   * `cache_control` to cache the whole array; with a large catalogue that is
   * where nearly all of the input-token saving comes from.
   */
  tools: BedrockToolDefinition[];
  /**
   * Execute one tool call and return its result as text. Throwing is allowed:
   * the message is fed back to the model as an errored tool_result.
   */
  runTool: (call: ToolLoopCall) => Promise<string> | string;
  /** Hard ceiling on model calls. Default DEFAULT_MAX_STEPS. */
  maxSteps?: number | undefined;
  /** Output ceiling per step. Default DEFAULT_MAX_TOKENS. */
  maxTokens?: number | undefined;
  temperature?: number | undefined;
  /** Wall-clock ceiling per step, not for the whole loop. */
  timeoutMs?: number | undefined;
  attribution?: AiAttribution | undefined;
  /** Called once per step with that step's usage. Never throws into the loop. */
  onUsage?: ((usage: TokenUsage) => void) | undefined;
  /** Observability hook, called after each step. Never throws into the loop. */
  onStep?: ((step: ToolLoopStep) => void) | undefined;
}

export interface ToolLoopStep {
  /** 1-based. */
  step: number;
  text: string;
  calls: ToolLoopCall[];
  usage: TokenUsage;
}

export interface ToolLoopResult {
  /** The model's final text. Empty when it never produced any. */
  text: string;
  /** Model calls made. Never more than maxSteps. */
  steps: number;
  /** Tool calls executed across every step. */
  toolCalls: number;
  /** True when the loop stopped because it ran out of steps, not because the model answered. */
  hitStepCeiling: boolean;
  /** Summed over every step. */
  usage: TokenUsage;
  /** The full transcript, including the assistant and tool_result turns the loop appended. */
  messages: ToolLoopMessage[];
}

/**
 * Eight is enough for a question that needs a handful of lookups and low enough
 * that a runaway loop is caught before it is expensive. Raise it deliberately.
 */
export const DEFAULT_MAX_STEPS = 8;
export const DEFAULT_MAX_TOKENS = 2048;

interface RawResponse {
  content?: Array<{
    type?: string;
    text?: string;
    id?: string;
    name?: string;
    input?: Record<string, unknown>;
  }>;
  stop_reason?: string;
}

/** Never let an observability callback break a call that already cost money. */
function safely(fn: (() => void) | undefined): void {
  if (!fn) return;
  try {
    fn();
  } catch {
    // Deliberately swallowed.
  }
}

export async function runToolLoop(
  creds: BedrockCredentials,
  req: ToolLoopRequest,
  signal?: AbortSignal,
): Promise<ToolLoopResult> {
  const maxSteps = Math.max(1, req.maxSteps ?? DEFAULT_MAX_STEPS);
  const messages: ToolLoopMessage[] = [...req.messages];

  let usage: TokenUsage = ZERO_USAGE;
  let toolCalls = 0;
  let text = "";
  let steps = 0;

  while (steps < maxSteps) {
    steps += 1;

    const payload: Record<string, unknown> = {
      anthropic_version: "bedrock-2023-05-31",
      max_tokens: req.maxTokens ?? DEFAULT_MAX_TOKENS,
      // Identical on every step, so the ephemeral cache turns the system prompt
      // from a per-step input cost into a read. Tenant data belongs in the
      // messages, which are never marked cacheable.
      system: [{ type: "text", text: req.system, cache_control: { type: "ephemeral" } }],
      messages,
      tools: req.tools,
      // "auto", not "any": the whole point is that the model may answer instead
      // of calling a tool. Force a tool here and the loop can never terminate
      // on its own.
      tool_choice: { type: "auto" },
      ...(req.temperature !== undefined ? { temperature: req.temperature } : {}),
    };

    const json = (await invokeModelRaw(
      creds,
      payload,
      { attribution: req.attribution, timeoutMs: req.timeoutMs ?? BEDROCK_TIMEOUT_MS },
      signal,
    )) as RawResponse;

    const stepUsage = extractUsage(json);
    usage = addUsage(usage, stepUsage);
    safely(req.onUsage ? () => req.onUsage?.(stepUsage) : undefined);

    const blocks = Array.isArray(json.content) ? json.content : [];

    // The final answer is the text of the LAST step that produced any, so a
    // mid-loop aside ("let me check that") never masquerades as the answer.
    const stepText = blocks
      .filter((b) => b.type === "text" && typeof b.text === "string")
      .map((b) => b.text as string)
      .join("");
    if (stepText.trim()) text = stepText;

    const calls: ToolLoopCall[] = blocks
      .filter((b) => b.type === "tool_use" && typeof b.name === "string")
      .map((b) => ({
        id: typeof b.id === "string" ? b.id : `${b.name as string}-${steps}`,
        name: b.name as string,
        // Absent for a zero-argument tool; treating that as "no call" would
        // silently drop every such tool, the same trap invokeTool documents.
        input: b.input ?? {},
      }));

    safely(
      req.onStep
        ? () => req.onStep?.({ step: steps, text: stepText, calls, usage: stepUsage })
        : undefined,
    );

    if (calls.length === 0) {
      return { text, steps, toolCalls, hitStepCeiling: false, usage, messages };
    }

    // Echo the assistant turn back verbatim. Anthropic rejects a tool_result
    // whose tool_use_id was never sent, so a reconstructed turn is not enough.
    messages.push({
      role: "assistant",
      content: blocks.filter((b) => b.type === "text" || b.type === "tool_use") as ToolLoopBlock[],
    });

    const results: ToolLoopBlock[] = [];
    for (const call of calls) {
      toolCalls += 1;
      try {
        const out = await req.runTool(call);
        results.push({ type: "tool_result", tool_use_id: call.id, content: String(out) });
      } catch (err) {
        results.push({
          type: "tool_result",
          tool_use_id: call.id,
          content: err instanceof Error ? err.message : String(err),
          is_error: true,
        });
      }
    }

    messages.push({ role: "user", content: results });
  }

  return { text, steps, toolCalls, hitStepCeiling: true, usage, messages };
}
